import React, { useState, useEffect, useRef } from 'react';
import { Button, Col, Input, Row, Select, Typography, DatePicker, Modal, Divider, Upload, Segmented } from "antd";
import styled from "styled-components";
import dayjs from 'dayjs';
import { CheckboxField } from './CheckboxField';
import { message } from 'antd';
import type { StandardType } from '../pages/tables/StandartSamples';
import type { Dayjs } from 'dayjs';
import type { DatePickerProps } from 'antd';
import type { UploadProps, UploadFile } from 'antd/es/upload/interface';
import { theme } from 'antd';
import { DeleteOutlined, UploadOutlined, DownloadOutlined } from '@ant-design/icons';
import { updateStandard, deleteStandard, downloadNormativeDocument, writeOffStandard } from '../api/chemicalRegistry';
import { useLaboratoryId } from '../hooks/useLaboratoryId';
import { getCustomColumns, CustomColumn } from '../api/customColumns';

const { Text } = Typography;

// Используем те же стили, что и в GetReagent
const ScreenContainer = styled.div<{ $isWriteOffMode?: boolean }>`
  width: 400px;
  height: ${props => props.$isWriteOffMode ? 'auto' : '500px'};
  min-height: ${props => props.$isWriteOffMode ? '300px' : '500px'};
  background: white;
  border: 1px solid #d9d9d9;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
`;

const ScrollContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0 16px;
  max-height: 500px;

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: #f0f0f0;
  }

  &::-webkit-scrollbar-thumb {
    background: #ccc;
    border-radius: 2px;
  }
`;

const BottomButtons = styled.div`
  padding: 16px;
`;

const StyledInput = styled(Input)`
  height: 40px;
  background-color: #fafafa;
  border-radius: 4px;

  &[type='number'] {
    -moz-appearance: textfield;
    
    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }
  }
`;

const StyledSelect = styled(Select)`
  .ant-select-selector {
    height: 40px !important;
    padding: 4px 11px !important;
    background-color: #fafafa !important;
    border-radius: 4px !important;
  }
`;

const StyledDatePicker = styled(DatePicker)`
  height: 40px;
  background-color: #fafafa;
  border-radius: 4px;
`;

const StyledText = styled(Text)`
  color: #8c8c8c;
  font-weight: 500;
  font-size: 12px;
`;

const ButtonsRow = styled(Row)`
  display: flex;
  justify-content: center;
  gap: 8px;
  
  .ant-btn {
    height: 40px;
  }

  .cancel-button {
    flex: 1;
    max-width: 178px;
  }

  .action-button {
    flex: 1;
    max-width: 178px;
  }
`;

// Добавляем опции
const standardTypes = [
    'ГСО 1234-56 Стандартный образец 1',
    'ОСО 7890-12 Стандартный образец 2',
    'СОП 3456-78 Стандартный образец 3',
].map(item => ({ label: item, value: item }));

const manufacturerOptions = [
    'ФГУП "ВНИИМ им. Д.И. Менделеева"',
    'ФГУП "УНИИМ"',
    'ООО "Химтест"',
].map(item => ({ label: item, value: item }));

const purposeOptions = [
    'Калибровка',
    'Поверка',
    'Контроль точности',
].map(item => ({ label: item, value: item }));

const indicatorOptions = [
    'Массовая доля железа',
    'Массовая доля углерода',
    'Массовая доля серы',
].map(item => ({ label: item, value: item }));

const notificationOptions = [
    'За месяц',
    'За 2 месяца',
    'За 3 месяца',
].map(item => ({ label: item, value: item }));

interface EditSSProps {
  onCancel: () => void;
  record: StandardType;
  onSuccess?: () => void;
}

const ScreenTitle = styled(Typography.Title)`
  margin: 16px !important;
  font-size: 16px !important;
`;

const HeaderContainer = styled.div`
  padding: 16px;
  
  .ant-segmented {
    .ant-segmented-item-label {
      font-size: 14px;
    }
  }
`;

// Добавляем интерфейс для формы списания
interface WriteOffFormHandle {
  handleSubmit: () => Promise<void>;
}

// Добавляем компонент формы списания
const StyledTextArea = styled(Input.TextArea)`
  background-color: #fafafa;
  border-radius: 4px;
  
  .ant-input {
    background-color: #fafafa;
  }
`;

const WriteOffForm = React.forwardRef<WriteOffFormHandle, { record: StandardType, onSuccess?: () => void, onCancel?: () => void }>(
  ({ record, onSuccess, onCancel }, ref) => {
    const [quantity, setQuantity] = useState<string>('');
    const [research, setResearch] = useState<string>('');
    const [loading, setLoading] = useState(false);
    const laboratoryId = useLaboratoryId();

    const handleSubmit = async () => {
      if (!quantity) {
        message.error('Пожалуйста, введите количество');
        return;
      }

      const numQuantity = parseFloat(quantity);
      if (isNaN(numQuantity) || numQuantity <= 0) {
        message.error('Количество должно быть положительным числом');
        return;
      }

      if (!laboratoryId) {
        message.error('ID лаборатории не найден');
        return;
      }

      setLoading(true);
      try {
        await writeOffStandard(
          laboratoryId.toString(),
          record.batchCode || '',
          record.packaging || '',
          numQuantity,
          research
        );
        // Очищаем форму после успешного списания
        setQuantity('');
        setResearch('');
        message.success('СО успешно списан');
        // Вызываем колбэки после успешного списания
        onSuccess?.();
        onCancel?.();
      } catch (error) {
        if (error instanceof Error) {
          message.error(error.message);
        } else {
          message.error('Ошибка при списании СО');
        }
      } finally {
        setLoading(false);
      }
    };

    // Expose handleSubmit method via ref
    React.useImperativeHandle(ref, () => ({
      handleSubmit
    }));

    return (
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <StyledText>Наименование</StyledText>
          <StyledInput 
            placeholder="наименование СО"
            value={record.name}
            disabled={true}
            style={{ 
              backgroundColor: '#f5f5f5',
              cursor: 'not-allowed',
              color: 'rgba(0, 0, 0, 0.65)'
            }}
          />
        </Col>

        <Col span={24}>
          <StyledText>Шифр партии</StyledText>
          <StyledInput 
            placeholder="шифр партии"
            value={record.batchCode}
            disabled={true}
            style={{ 
              backgroundColor: '#f5f5f5',
              cursor: 'not-allowed',
              color: 'rgba(0, 0, 0, 0.65)'
            }}
          />
        </Col>

        <Col span={24}>
          <StyledText>Фасовка</StyledText>
          <StyledInput 
            placeholder="фасовка"
            value={record.packaging}
            disabled={true}
            style={{ 
              backgroundColor: '#f5f5f5',
              cursor: 'not-allowed',
              color: 'rgba(0, 0, 0, 0.65)'
            }}
          />
        </Col>

        <Col span={24}>
          <StyledText>Количество</StyledText>
          <StyledInput 
            type="number"
            placeholder="введите значение"
            value={quantity}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setQuantity(e.target.value)}
          />
        </Col>

        <Col span={24}>
          <StyledText>Исследование</StyledText>
          <StyledTextArea 
            placeholder="описание"
            style={{ height: 63, resize: 'none' }}
            autoSize={false}
            value={research}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setResearch(e.target.value)}
          />
        </Col>
      </Row>
    );
  }
);

const EditSS: React.FC<EditSSProps> = ({ onCancel, record, onSuccess }) => {
  const laboratoryId = useLaboratoryId();
  const { token } = theme.useToken();
  const [loading, setLoading] = useState(false);
  const [editedRecord, setEditedRecord] = useState<StandardType>(record);
  const [customColumns, setCustomColumns] = useState<CustomColumn[]>([]);
  const [customFieldValues, setCustomFieldValues] = useState<Record<string, any>>(record.custom_columns || {});
  const [normativeDocumentFile, setNormativeDocumentFile] = useState<File | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [activeTab, setActiveTab] = useState<string | number>('edit');
  const formRef = useRef<WriteOffFormHandle>(null);

  // Добавляем эффект для обновления editedRecord при изменении record
  useEffect(() => {
    setEditedRecord(record);
    setCustomFieldValues(record.custom_columns || {});
  }, [record]);
  
  // Добавляем эффект для загрузки кастомных колонок
  useEffect(() => {
    const fetchCustomColumns = async () => {
      if (!laboratoryId) return;
      
      try {
        const columns = await getCustomColumns('standard', Number(laboratoryId));
        setCustomColumns(columns);
      } catch (error) {
        console.error('Ошибка при загрузке кастомных колонок:', error);
      }
    };
    
    fetchCustomColumns();
  }, [laboratoryId]);

  // Типизированные обработчики
  const handleSelectChange = (field: keyof StandardType) => (value: unknown) => {
    setEditedRecord(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleDateChange = (field: keyof StandardType) => 
    (_: unknown, dateString: string | string[]) => {
      setEditedRecord(prev => ({
        ...prev,
        [field]: typeof dateString === 'string' ? dateString || null : null
      }));
    };
    
  // Добавляем обработчик для кастомных полей
  const handleCustomFieldChange = (key: string, value: any) => {
    setCustomFieldValues(prev => ({
      ...prev,
      [key]: value
    }));
  };

  // Обработчик для загрузки файла
  const handleFileUpload: UploadProps['onChange'] = (info) => {
    if (info.file.status === 'uploading') {
      return;
    }
    
    if (info.file.status === 'done') {
      message.success(`${info.file.name} успешно загружен`);
    } else if (info.file.status === 'error') {
      message.error(`Ошибка загрузки файла ${info.file.name}.`);
    }
    
    // Проверка размера файла (20MB = 20 * 1024 * 1024 bytes)
    const maxSize = 20 * 1024 * 1024; // 20MB
    
    if (info.file.originFileObj && info.file.originFileObj.size > maxSize) {
      message.error('Размер файла не должен превышать 20MB');
      return;
    }
    
    if (info.file.originFileObj) {
      setNormativeDocumentFile(info.file.originFileObj);
      setEditedRecord(prev => ({
        ...prev,
        normativeDocument: info.file.name
      }));
    }
  };
  
  // Обработчик для удаления файла
  const handleRemoveFile = () => {
    setNormativeDocumentFile(null);
    setEditedRecord(prev => ({
      ...prev,
      normativeDocument: ''
    }));
  };
  
  // Функция для скачивания файла
  const handleDownloadDocument = async () => {
    if (!laboratoryId || !editedRecord.key) return;
    
    try {
      setIsDownloading(true);
      const fileBlob = await downloadNormativeDocument(laboratoryId, editedRecord.key as number);
      
      // Получаем имя файла - берем последнюю часть пути или весь текст, если разделителей нет
      const fileName = typeof editedRecord.normativeDocument === 'string' 
        ? editedRecord.normativeDocument.split('/').pop() || editedRecord.normativeDocument 
        : 'document';
      
      // Создаем ссылку для скачивания файла
      const url = window.URL.createObjectURL(fileBlob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      message.success('Файл успешно скачан');
    } catch (error) {
      message.error('Ошибка при скачивании файла');
      console.error('Error downloading file:', error);
    } finally {
      setIsDownloading(false);
    }
  };
  
  // Конфигурация для Ant Design Upload
  const uploadProps: UploadProps = {
    beforeUpload: (file) => {
      const maxSize = 20 * 1024 * 1024; // 20MB
      if (file.size > maxSize) {
        message.error('Размер файла не должен превышать 20MB');
        return Upload.LIST_IGNORE;
      }
      return false; // Предотвращаем автоматическую загрузку
    },
    onChange: handleFileUpload,
    onRemove: handleRemoveFile,
    maxCount: 1,
    showUploadList: false,
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      
      if (!laboratoryId) {
        message.error('Laboratory ID not found');
        return;
      }

      // Создаем FormData для отправки файла
      const formData = new FormData();
      
      // Добавляем файл, если он есть
      if (normativeDocumentFile) {
        formData.append('normative_document', normativeDocumentFile);
      }
      
      // Добавляем остальные данные
      const dataToSend = {
        name: editedRecord.name,
        manufacturer: editedRecord.manufacturer,
        purpose: editedRecord.purpose,
        batch_code: editedRecord.batchCode,
        packaging: editedRecord.packaging,
        indicator: editedRecord.indicator,
        certified_value: editedRecord.certifiedValue,
        certified_value_unit: editedRecord.certifiedValueUnit,
        error_value: editedRecord.errorValue,
        error_value_unit: editedRecord.errorValueUnit,
        additional_info: editedRecord.additionalInfo,
        normative_document: editedRecord.normativeDocument,
        expiration_date: editedRecord.expirationDate ? dayjs(editedRecord.expirationDate).format('YYYY-MM-DD') : null,
        expiration_notification: editedRecord.expirationNotification,
        release_date: editedRecord.releaseDate ? dayjs(editedRecord.releaseDate).format('YYYY-MM-DD') : null,
        notes: editedRecord.notes,
        is_precursor: editedRecord.isPrecursor,
        minimum_remaining: editedRecord.minimumRemaining || 10,
        storage_location: editedRecord.storageLocation || null,
        storage_conditions: editedRecord.storageConditions || null,
        opened_shelf_life: editedRecord.openedShelfLife,
        opened_date: editedRecord.openedDate ? dayjs(editedRecord.openedDate).format('YYYY-MM-DD') : null,
        used: editedRecord.used || 0,
        remaining: editedRecord.remaining || 0,
        custom_columns: customFieldValues
      };
      
      formData.append('standard_data', JSON.stringify(dataToSend));
      
      await updateStandard(Number(laboratoryId), editedRecord.key as number, formData);
      
      message.success('Стандарт успешно обновлен');
      if (onSuccess) {
        onSuccess();
      }
      onCancel();
    } catch (error) {
      console.error('Error updating standard:', error);
      message.error('Ошибка при обновлении стандарта');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = () => {
    Modal.confirm({
      title: 'Подтверждение удаления',
      content: 'Вы уверены, что хотите удалить эту запись?',
      okText: 'Удалить',
      cancelText: 'Отмена',
      centered: true,
      icon: null,
      okButtonProps: { danger: true },
      width: 500,
      onOk: async () => {
        if (!laboratoryId) return;

        try {
          setLoading(true);
          await deleteStandard(laboratoryId, record.key as number);
          message.success('Запись успешно удалена');
          onSuccess?.();
          onCancel();
        } catch (error) {
          if (error instanceof Error) {
            message.error(error.message);
          } else {
            message.error('Ошибка при удалении записи');
          }
          console.error('Error:', error);
        } finally {
          setLoading(false);
        }
      }
    });
  };

  const handleContainerClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  const handleOpenChange = (open: boolean) => {
    if (open) {
      const handleClickOutside = (e: MouseEvent) => {
        e.stopPropagation();
      };
      document.addEventListener('mousedown', handleClickOutside);
      setTimeout(() => {
        document.removeEventListener('mousedown', handleClickOutside);
      }, 0);
    }
  };

  // Функция для рендеринга кастомных полей
  const renderCustomFields = () => {
    return customColumns.map(column => {
      const value = customFieldValues[column.key] || '';
      
      switch (column.type) {
        case 'text':
          return (
            <Col span={24} key={column.key}>
              <StyledText>{column.title}</StyledText>
              <StyledInput
                value={value}
                onChange={(e) => handleCustomFieldChange(column.key, e.target.value)}
                placeholder="введите значение"
                onMouseDown={handleMouseDown}
                onClick={e => e.stopPropagation()}
              />
            </Col>
          );
        case 'int':
          return (
            <Col span={24} key={column.key}>
              <StyledText>{column.title}</StyledText>
              <StyledInput
                type="number"
                value={value}
                onChange={(e) => handleCustomFieldChange(column.key, e.target.value)}
                placeholder="введите число"
                onMouseDown={handleMouseDown}
                onClick={e => e.stopPropagation()}
              />
            </Col>
          );
        case 'date':
          return (
            <Col span={24} key={column.key}>
              <StyledText>{column.title}</StyledText>
              <StyledDatePicker
                value={value ? dayjs(value) : null}
                onChange={(date: any) => {
                  if (date) {
                    handleCustomFieldChange(column.key, date.toISOString());
                  } else {
                    handleCustomFieldChange(column.key, null);
                  }
                }}
                format="DD.MM.YYYY"
                style={{ width: '100%' }}
                onMouseDown={handleMouseDown}
                onClick={e => e.stopPropagation()}
                onOpenChange={handleOpenChange}
                popupStyle={{ zIndex: 1100 }}
                getPopupContainer={(trigger) => trigger.parentElement || document.body}
              />
            </Col>
          );
        case 'boolean':
          return (
            <Col span={24} key={column.key}>
              <StyledText>{column.title}</StyledText>
              <CheckboxField
                title="Да"
                checked={Boolean(value)}
                onChange={(checked) => handleCustomFieldChange(column.key, checked)}
              />
            </Col>
          );
        default:
          return null;
      }
    });
  };

  const handleWriteOffClick = () => {
    formRef.current?.handleSubmit();
  };

  return (
    <ScreenContainer 
      $isWriteOffMode={activeTab === 'write-off'}
      onClick={handleContainerClick}
    >
      <HeaderContainer>
        <Segmented
          options={[
            { label: 'Списать СО', value: 'write-off' },
            { label: 'Изменение данных', value: 'edit' }
          ]}
          value={activeTab}
          onChange={setActiveTab}
          size="large"
          block
        />
      </HeaderContainer>
      
      <ScrollContent>
        {activeTab === 'write-off' ? (
          <WriteOffForm 
            record={record}
            ref={formRef}
            onSuccess={onSuccess}
            onCancel={onCancel}
          />
        ) : (
          <Row gutter={[16, 16]}>
            <Col span={24}>
              <StyledText>Наименование, тип, номер и категория СО</StyledText>
              <StyledInput
                value={editedRecord.name}
                onChange={(e) => setEditedRecord(prev => ({ ...prev, name: e.target.value }))}
                placeholder="введите название"
              />
            </Col>

            <Col span={24}>
              <StyledText>Изготовитель СО</StyledText>
              <StyledInput
                value={editedRecord.manufacturer}
                onChange={(e) => setEditedRecord(prev => ({ ...prev, manufacturer: e.target.value }))}
                placeholder="введите изготовителя"
              />
            </Col>

            <Col span={24}>
              <StyledText>Назначение</StyledText>
              <StyledInput
                value={editedRecord.purpose}
                onChange={(e) => setEditedRecord(prev => ({ ...prev, purpose: e.target.value }))}
                placeholder="введите назначение"
              />
            </Col>

            <Col span={24}>
              <StyledText>Шифр партии</StyledText>
              <StyledInput
                value={editedRecord.batchCode}
                onChange={(e) => setEditedRecord(prev => ({ ...prev, batchCode: e.target.value }))}
                placeholder="введите шифр партии"
                onMouseDown={handleMouseDown}
                onClick={e => e.stopPropagation()}
              />
            </Col>

            <Col span={24}>
              <StyledText>Фасовка</StyledText>
              <StyledInput
                value={editedRecord.packaging}
                onChange={(e) => setEditedRecord(prev => ({ ...prev, packaging: e.target.value }))}
                placeholder="введите фасовку"
                onMouseDown={handleMouseDown}
                onClick={e => e.stopPropagation()}
              />
            </Col>

            <Col span={24}>
              <StyledText>Показатель</StyledText>
              <StyledInput
                value={editedRecord.indicator}
                onChange={(e) => setEditedRecord(prev => ({ ...prev, indicator: e.target.value }))}
                placeholder="введите показатель"
              />
            </Col>

            <Col span={24}>
              <StyledText>Аттестованное значение м.д. %</StyledText>
              <StyledInput
                type="number"
                value={editedRecord.certifiedValue}
                onChange={(e) => setEditedRecord(prev => ({ ...prev, certifiedValue: Number(e.target.value) }))}
                placeholder="введите значение"
                onMouseDown={handleMouseDown}
                onClick={e => e.stopPropagation()}
              />
            </Col>

            <Col span={24}>
              <StyledText>Единица измерения атт. значения</StyledText>
              <StyledInput
                value={editedRecord.certifiedValueUnit}
                onChange={(e) => setEditedRecord(prev => ({ ...prev, certifiedValueUnit: e.target.value }))}
                placeholder="введите единицу измерения"
                onMouseDown={handleMouseDown}
                onClick={e => e.stopPropagation()}
              />
            </Col>

            <Col span={24}>
              <StyledText>Погрешность а.з. абс. %</StyledText>
              <StyledInput
                type="number"
                value={editedRecord.errorValue}
                onChange={(e) => setEditedRecord(prev => ({ ...prev, errorValue: Number(e.target.value) }))}
                placeholder="введите значение"
                onMouseDown={handleMouseDown}
                onClick={e => e.stopPropagation()}
              />
            </Col>

            <Col span={24}>
              <StyledText>Единица измерения погрешности</StyledText>
              <StyledInput
                value={editedRecord.errorValueUnit}
                onChange={(e) => setEditedRecord(prev => ({ ...prev, errorValueUnit: e.target.value }))}
                placeholder="введите единицу измерения"
                onMouseDown={handleMouseDown}
                onClick={e => e.stopPropagation()}
              />
            </Col>

            <Col span={24}>
              <StyledText>Дополнительные сведения</StyledText>
              <StyledInput
                value={editedRecord.additionalInfo}
                onChange={(e) => setEditedRecord(prev => ({ ...prev, additionalInfo: e.target.value }))}
                placeholder="введите информацию"
                onMouseDown={handleMouseDown}
                onClick={e => e.stopPropagation()}
              />
            </Col>

            <Col span={24}>
              <StyledText>Нормативный документ</StyledText>
              <Row gutter={8} align="middle">
                <Col flex="auto">
                  <StyledInput
                    value={editedRecord.normativeDocument || 'Файл не выбран'}
                    readOnly
                    placeholder="Файл не выбран"
                  />
                </Col>
                <Col>
                  <Upload
                    accept=".pdf,.doc,.docx,.txt"
                    showUploadList={false}
                    beforeUpload={(file) => {
                      const maxSize = 20 * 1024 * 1024; // 20MB
                      if (file.size > maxSize) {
                        message.error('Размер файла не должен превышать 20MB');
                        return Upload.LIST_IGNORE;
                      }
                      setNormativeDocumentFile(file);
                      setEditedRecord(prev => ({
                        ...prev,
                        normativeDocument: file.name
                      }));
                      return false;
                    }}
                  >
                    <Button style={{ height: 40 }} icon={<UploadOutlined />}>Выбрать</Button>
                  </Upload>
                </Col>
                {editedRecord.normativeDocument && (
                  <>
                    <Col>
                      <Button
                        style={{ height: 40 }}
                        icon={<DeleteOutlined />}
                        onClick={() => {
                          setNormativeDocumentFile(null);
                          setEditedRecord(prev => ({
                            ...prev,
                            normativeDocument: ''
                          }));
                        }}
                      />
                    </Col>
                  </>
                )}
              </Row>
              <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: 4 }}>
                До 20МБ. Форматы: PDF, DOC, DOCX, TXT
              </div>
            </Col>

            <Col span={24}>
              <StyledText>Срок годности экземпляра СО</StyledText>
              <StyledDatePicker
                value={editedRecord.expirationDate ? dayjs(editedRecord.expirationDate) : null}
                onChange={(date: any) => {
                  setEditedRecord(prev => ({
                    ...prev,
                    expirationDate: date ? date.format('YYYY-MM-DD') : ''
                  }));
                }}
                format="DD.MM.YYYY"
                style={{ width: '100%' }}
                onMouseDown={handleMouseDown}
                onClick={e => e.stopPropagation()}
                onOpenChange={handleOpenChange}
                popupStyle={{ zIndex: 1100 }}
                getPopupContainer={(trigger) => trigger.parentElement || document.body}
              />
            </Col>

            <Col span={24}>
              <StyledText>Уведомление об окончании срока годности</StyledText>
              <StyledSelect
                value={editedRecord.expirationNotification}
                onChange={handleSelectChange('expirationNotification')}
                options={notificationOptions}
                style={{ width: '100%' }}
                showSearch
                allowClear
                onMouseDown={handleMouseDown}
                onClick={e => e.stopPropagation()}
                dropdownStyle={{ zIndex: 1100 }}
              />
            </Col>

            <Col span={24}>
              <StyledText>Дата выпуска экземпляра СО</StyledText>
              <StyledDatePicker
                value={editedRecord.releaseDate ? dayjs(editedRecord.releaseDate) : null}
                onChange={(date: any) => {
                  setEditedRecord(prev => ({
                    ...prev,
                    releaseDate: date ? date.format('YYYY-MM-DD') : ''
                  }));
                }}
                format="DD.MM.YYYY"
                style={{ width: '100%' }}
                onMouseDown={handleMouseDown}
                onClick={e => e.stopPropagation()}
                onOpenChange={handleOpenChange}
                popupStyle={{ zIndex: 1100 }}
                getPopupContainer={(trigger) => trigger.parentElement || document.body}
              />
            </Col>

            <Col span={24}>
              <StyledText>Примечание</StyledText>
              <StyledInput
                value={editedRecord.notes}
                onChange={(e) => setEditedRecord(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="введите примечание"
                onMouseDown={handleMouseDown}
                onClick={e => e.stopPropagation()}
              />
            </Col>

            <Col span={24}>
              <StyledText>Место хранения</StyledText>
              <StyledInput
                value={editedRecord.storageLocation || ''}
                onChange={(e) => setEditedRecord(prev => ({ ...prev, storageLocation: e.target.value || null }))}
                placeholder="введите место хранения"
                onMouseDown={handleMouseDown}
                onClick={e => e.stopPropagation()}
              />
            </Col>

            <Col span={24}>
              <StyledText>Условия хранения</StyledText>
              <StyledInput
                value={editedRecord.storageConditions || ''}
                onChange={(e) => setEditedRecord(prev => ({ ...prev, storageConditions: e.target.value || null }))}
                placeholder="введите условия хранения"
                onMouseDown={handleMouseDown}
                onClick={e => e.stopPropagation()}
              />
            </Col>

            <Col span={24}>
              <StyledText>Срок хранения после вскрытия (дней)</StyledText>
              <Input
                type="number"
                value={editedRecord.openedShelfLife?.toString() || ''}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const value = e.target.value ? parseInt(e.target.value) : null;
                  setEditedRecord((prev: StandardType) => ({
                    ...prev,
                    openedShelfLife: value
                  }));
                }}
                placeholder="введите срок хранения"
                onMouseDown={handleMouseDown}
                onClick={e => e.stopPropagation()}
              />
            </Col>

            <Col span={24}>
              <StyledText>Дата вскрытия</StyledText>
              <DatePicker
                value={editedRecord.openedDate ? dayjs(editedRecord.openedDate) : null}
                onChange={(date: Dayjs | null) => {
                  setEditedRecord((prev: StandardType) => ({
                    ...prev,
                    openedDate: date ? date.format('YYYY-MM-DD') : null
                  }));
                }}
                format="DD.MM.YYYY"
                style={{ width: '100%' }}
                placeholder="Выберите дату"
                onMouseDown={handleMouseDown}
                onClick={e => e.stopPropagation()}
                onOpenChange={handleOpenChange}
                popupStyle={{ zIndex: 1100 }}
                getPopupContainer={(trigger) => trigger.parentElement || document.body}
              />
            </Col>

            <Col span={24}>
              <StyledText>Использовано</StyledText>
              <StyledInput
                type="number"
                value={editedRecord.used}
                onChange={(e) => setEditedRecord(prev => ({ ...prev, used: parseFloat(e.target.value) || 0 }))}
                placeholder="введите количество"
                onMouseDown={handleMouseDown}
                onClick={e => e.stopPropagation()}
              />
            </Col>

            <Col span={24}>
              <StyledText>Остаток</StyledText>
              <StyledInput
                type="number"
                value={editedRecord.remaining}
                onChange={(e) => setEditedRecord(prev => ({ ...prev, remaining: parseFloat(e.target.value) || 0 }))}
                placeholder="введите количество"
                onMouseDown={handleMouseDown}
                onClick={e => e.stopPropagation()}
              />
            </Col>

            <Col span={24}>
              <StyledText>Минимальный остаток</StyledText>
              <StyledInput
                type="number"
                value={editedRecord.minimumRemaining || 10}
                onChange={(e) => setEditedRecord(prev => ({ 
                  ...prev, 
                  minimumRemaining: parseFloat(e.target.value) || 10 
                }))}
                placeholder="введите значение"
                onMouseDown={handleMouseDown}
                onClick={e => e.stopPropagation()}
              />
            </Col>

            <Col span={24}>
              <StyledText>Прекурсор</StyledText>
              <CheckboxField
                title="Да"
                checked={editedRecord.isPrecursor}
                onChange={(checked) => setEditedRecord(prev => ({ ...prev, isPrecursor: checked }))}
              />
            </Col>
            
            {/* Рендерим кастомные поля */}
            {renderCustomFields()}
          </Row>
        )}
      </ScrollContent>

      <BottomButtons>
        <ButtonsRow>
          {activeTab === 'edit' && (
            <DeleteButton 
              icon={<DeleteOutlined style={{ color: token['red-5'] }} />}
              onClick={handleDelete}
            />
          )}
          <Button 
            className="cancel-button"
            onClick={onCancel}
            disabled={loading}
          >
            Отмена
          </Button>
          <Button 
            className="action-button"
            type="primary"
            loading={loading}
            onClick={activeTab === 'write-off' ? handleWriteOffClick : handleSave}
          >
            {activeTab === 'write-off' ? 'Списать' : 'Сохранить'}
          </Button>
        </ButtonsRow>
      </BottomButtons>
    </ScreenContainer>
  );
};

// Добавляем стили для кнопки удаления
const DeleteButton = styled(Button)`
  min-width: 46px !important;
  width: 46px !important;
  height: 40px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 46px !important;
`;

export default EditSS;

